import { toJS } from "mobx";
import { observer } from "mobx-react";
import { Box, useTheme } from "@mui/material";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from "recharts";
import FormSection from "../../../forms/FormSection";
import useFeature from "../../../useFeature";
import { useApplication } from "../../../ApplicationProvider";
import ChartSkeleton from "./ChartSkeleton";
import RevenueBurnupFeature from "./RevenueBurnupFeature";

const CHART_BOX = { p: 2, height: 360 };
const TITLE = "Facturación acumulada vs presupuesto del período";

function formatMoney(value) {
    if (value == null) return "";
    const absoluteValue = Math.abs(value);
    if (absoluteValue >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (absoluteValue >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(value / 1000)}k`;
}

function formatBoxes(value) {
    if (value == null) return "";
    const absoluteValue = Math.abs(value);
    if (absoluteValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (absoluteValue >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return `${Math.round(value)}`;
}

function seriesLabelsIn(boxesMode) {
    return {
        invoiced: boxesMode ? "Facturado (Cajas)" : "Facturado (invoices)",
        projected: "Proyectado (pedidos)",
        target: "Directriz",
    };
}

/**
 * Expected data: array of { day, label, invoiced, projected, target }
 * - day:       number — day of the month.
 * - label:     string — date (DD/MM) rendered on the X axis.
 * - invoiced:  cumulative facturación from invoices for past days; null on future days.
 * - projected: cumulative facturación including pending orders from today onward; null on past
 *              days. Its value on "today" equals `invoiced` so both segments join visually.
 * - target:    directriz — cumulative prorated budget for each day across the whole period.
 *              Its final value is the period budget, drawn as the horizontal "Presupuesto" line.
 */
function RevenueBurnupChart({ filters, boxesMode }) {
    const application = useApplication();
    const feature = useFeature(
        () => new RevenueBurnupFeature(application, filters),
        [application, JSON.stringify(filters)]
    );
    const theme = useTheme();

    if (!feature?.loaded) {
        return (
            <FormSection title={TITLE}>
                <ChartSkeleton />
            </FormSection>
        );
    }

    const formatValue = boxesMode ? formatBoxes : formatMoney;
    const seriesLabels = seriesLabelsIn(boxesMode);

    const data = toJS(feature.revenueBurnup).map((entry) => ({
        day: entry.day,
        label: entry.label,
        invoiced: boxesMode ? entry.invoicedBoxes : entry.invoiced,
        projected: boxesMode ? entry.projectedBoxes : entry.projected,
        target: boxesMode ? entry.targetBoxes : entry.target,
    }));
    const budgetTarget = data.reduce((maxTarget, entry) => Math.max(maxTarget, entry.target ?? 0), 0);
    const budgetColor = theme.palette.error?.main || "#C62828";
    const directrixColor = theme.palette.text?.secondary || "#9E9E9E";

    return (
        <FormSection title={TITLE}>
            <Box sx={CHART_BOX}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis
                            width={80}
                            tickFormatter={formatValue}
                            domain={[
                                (dataMin) => Math.min(0, Math.floor(dataMin * 1.05)),
                                (dataMax) => Math.ceil(Math.max(dataMax, budgetTarget) * 1.05),
                            ]}
                        />
                        <Tooltip
                            formatter={(value, name) => [formatValue(value), seriesLabels[name] || name]}
                            labelFormatter={(label) => `Día ${label}`}
                        />
                        <Legend formatter={(name) => seriesLabels[name] || name} />
                        <ReferenceLine
                            y={budgetTarget}
                            stroke={budgetColor}
                            strokeWidth={2}
                            label={{
                                value: "Presupuesto",
                                position: "insideTopRight",
                                fill: budgetColor,
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="target"
                            stroke={directrixColor}
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="invoiced"
                            stroke={theme.palette.primary?.main || "#8B4513"}
                            strokeWidth={2.5}
                            dot={false}
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="projected"
                            stroke={theme.palette.secondary?.main || "#D2691E"}
                            strokeWidth={2.5}
                            strokeDasharray="5 5"
                            dot={false}
                            connectNulls={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </FormSection>
    );
}

export default observer(RevenueBurnupChart);
